import React, { FunctionComponent, useState } from 'react';
import { AgFinancingPhase4Status, AgGroup } from '@indigo-ag/schema';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloError, useMutation } from '@apollo/client';

import i18next from 'core/i18n';
import { PhaseSection, text } from 'shared';
import { PhaseWrapper } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/PhaseWrapper';
import { CessionActionButton } from './CessionActionButton/CessionActionButton';
import { NegotiationsList } from '../shared/NegotiationsList/NegotiationsList';
import { CessionGroupTimeline } from './CessionGroupTimeline/CessionGroupTimeline';
import { CessionGroupInformation } from './CessionGroupInformation/CessionGroupInformation';
import { CessionGroupState } from './CessionGroupState/CessionGroupState';
import { FundInfo } from './FundInfo/FundInfo';
import { haveToSendToFund } from '../BrazilGrowerFinance/utils/cessionGroup.util';
import { changeCessionGroupStatus } from '../../../../features/brazil-grower/queries';

interface CessionGroupDetailsProps {
  cessionGroup: AgGroup;
}

export const CessionGroupDetails: FunctionComponent<CessionGroupDetailsProps> = ({ cessionGroup }) => {
  /**
   * Mutations
   */
  const [changeCessionGroupStatusFn] = useMutation(changeCessionGroupStatus);

  /**
   * State
   */
  const [isResending, setIsResending] = useState(false);

  const { addFlashMessage } = useFlashMessage();

  const onResendToFund = () => {
    setIsResending(true);

    return changeCessionGroupStatusFn({
      variables: {
        agGroupId: cessionGroup.id,
        status: AgFinancingPhase4Status.ASSIGNED_TO_FUND,
        notes: undefined,
        user: undefined,
        isResending: true
      }
    })
      .then(response => {
        if (haveToSendToFund(response?.data?.changeCessionGroupStatus, false)) {
          addFlashMessage({
            content: text.firstLetterUppercase(i18next.t('fundInfoNotUpdatedAPI')),
            title: '',
            variant: 'error'
          });
        } else {
          addFlashMessage({
            content: text.firstLetterUppercase(i18next.t('resentToFund')),
            title: '',
            variant: 'success'
          });
        }
      })
      .catch((e: ApolloError) => {
        addFlashMessage({
          content: e.graphQLErrors[0].message,
          title: '',
          variant: 'error'
        });
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  return (
    <div className="flex flex-space-btw mt-4">
      <CessionGroupTimeline cessionGroup={cessionGroup} />
      <PhaseWrapper title={i18next.t('cession')} actionElement={<CessionActionButton agGroup={cessionGroup} />}>
        <CessionGroupState cessionGroup={cessionGroup} />
        {cessionGroup.status === AgFinancingPhase4Status.AWAITING_ASSIGNMENT ||
        cessionGroup.status === AgFinancingPhase4Status.ASSIGNED_TO_FUND ? null : (
          <FundInfo group={cessionGroup} />
        )}
        <PhaseSection
          title={i18next.t('contracts')}
          topRightContent={
            haveToSendToFund(cessionGroup, false) && (
              <Button
                size="small"
                className="text-transform-capitalize"
                onClick={onResendToFund}
                busy={isResending}
                variant="primary"
              >
                {text.toTitleCase(i18next.t('resendToFund'))}
              </Button>
            )
          }
        >
          <NegotiationsList
            negotiations={cessionGroup.agFinancingProcesses}
            canRemoveNegotiations={
              cessionGroup.status === AgFinancingPhase4Status.AWAITING_ASSIGNMENT ||
              cessionGroup.status === AgFinancingPhase4Status.RECEIVED_BY_FUND
            }
          />
        </PhaseSection>
      </PhaseWrapper>
      <CessionGroupInformation cessionGroup={cessionGroup} />
    </div>
  );
};
